/**
 * Sharing with people — the Drive BFF's per-person access (viewer/editor,
 * download allowed or blocked) and "Shared with me". Share *links* stay in
 * driveApi.ts.
 */
const baseUrl = import.meta.env.VITE_DRIVE_BFF_BASE_URL as string;

export type ShareRole = "viewer" | "editor";

export interface PersonAccess {
    id: string;
    email: string;
    role: ShareRole;
    /** Always true for editors; false = view-only, download blocked. */
    allowDownload: boolean;
    grantedBy: string;
    createdAt: string;
    /** "drive" | "connect-chat" | "connect-channel" | "connect-recording" */
    source: string | null;
}

export interface ItemSharing {
    itemId: string;
    ownerEmail: string;
    /** The caller's own access: owner, editor or viewer. */
    myRole: "owner" | ShareRole;
    /** Owners and editors can add, change and remove people. */
    canManage: boolean;
    people: PersonAccess[];
}

export interface SharedItem {
    item: {
        id: string;
        parentId: string | null;
        kind: "Folder" | "File";
        name: string;
        contentType: string | null;
        sizeBytes: number;
        createdAt: string;
        updatedAt: string;
        ownerEmail?: string;
        role?: ShareRole;
        canDownload?: boolean;
    };
    permissionId: string;
    sharedBy: string;
    sharedAt: string;
    source: string | null;
}

async function call<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
        res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers, credentials: "omit" });
    } catch {
        throw new Error("Can't reach Drive — check your connection.");
    }
    if (!res.ok) {
        let message = res.status === 404 ? "That item no longer exists." : `Request failed (${res.status})`;
        try {
            const body = (await res.json()) as { title?: string; detail?: string };
            message = body.detail?.trim() || body.title?.trim() || message;
        } catch { /* not JSON */ }
        throw new Error(message);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

const enc = encodeURIComponent;

export const sharingApi = {
    get: (itemId: string, token: string | undefined) =>
        call<ItemSharing>(`/api/items/${enc(itemId)}/permissions`, token),

    add: (
        itemId: string,
        body: { emails: string[]; role: ShareRole; allowDownload: boolean },
        token: string | undefined
    ) =>
        call<ItemSharing>(`/api/items/${enc(itemId)}/permissions`, token, {
            method: "POST",
            body: JSON.stringify({ ...body, source: "drive" })
        }),

    update: (permissionId: string, body: { role?: ShareRole; allowDownload?: boolean }, token: string | undefined) =>
        call<PersonAccess>(`/api/permissions/${enc(permissionId)}`, token, {
            method: "PATCH",
            body: JSON.stringify(body)
        }),

    remove: (permissionId: string, token: string | undefined) =>
        call<void>(`/api/permissions/${enc(permissionId)}`, token, { method: "DELETE" }),

    sharedWithMe: (token: string | undefined) =>
        call<SharedItem[]>("/api/shared-with-me", token)
};

/** Loose email check for the "Add people" box — the server re-validates. */
export function looksLikeEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
